import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Avatar, 
  Divider,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  CircularProgress,
  Alert
} from '@mui/material';
import { styled } from '@mui/system';
import SendIcon from '@mui/icons-material/Send';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { supabase } from '../supabaseClient';
import { v4 as uuidv4 } from 'uuid';

const CommentBox = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.spacing(1),
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
  marginBottom: theme.spacing(2),
  position: 'relative',
  transition: 'all 0.2s ease',
  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
  }
}));

const CommentSection = ({ itemId, itemType, shareId, canEdit = false }) => {
  const [comments, setComments] = useState([]);
  const [newComment, setNewComment] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedComment, setSelectedComment] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editedComment, setEditedComment] = useState('');
  const [userName, setUserName] = useState('Misafir');
  const [userEmail, setUserEmail] = useState('');

  useEffect(() => {
    fetchComments();
  }, [itemId, itemType]);

  const fetchComments = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const { data, error } = await supabase
        .from('comments')
        .select('*')
        .eq('item_id', itemId)
        .eq('item_type', itemType)
        .order('created_at', { ascending: true });
      
      if (error) throw error;
      
      setComments(data || []);
    } catch (err) {
      console.error('Error fetching comments:', err);
      setError('Yorumlar yüklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmitComment = async () => {
    if (!newComment.trim()) return;
    
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);
      
      const commentId = uuidv4();
      const commentData = {
        id: commentId,
        item_id: itemId,
        item_type: itemType,
        share_id: shareId,
        user_name: userName,
        user_email: userEmail,
        content: newComment,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      };
      
      const { error } = await supabase
        .from('comments')
        .insert(commentData);
      
      if (error) throw error;
      
      // Add the new comment to the list
      setComments(prev => [...prev, commentData]);
      setNewComment('');
      setSuccess('Yorumunuz başarıyla eklendi.');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
      
    } catch (err) {
      console.error('Error submitting comment:', err);
      setError('Yorum eklenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleMenuOpen = (event, comment) => {
    setAnchorEl(event.currentTarget);
    setSelectedComment(comment);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEditComment = () => {
    setEditMode(true);
    setEditedComment(selectedComment.content);
    handleMenuClose();
  };

  const handleSaveEdit = async () => {
    if (!editedComment.trim() || !selectedComment) return;
    
    try {
      setLoading(true);
      
      const { error } = await supabase
        .from('comments')
        .update({ 
          content: editedComment,
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedComment.id);
      
      if (error) throw error;
      
      // Update the comment in the list
      setComments(prev => 
        prev.map(comment => 
          comment.id === selectedComment.id 
            ? { ...comment, content: editedComment, updated_at: new Date().toISOString() } 
            : comment
        )
      );
      
      setEditMode(false);
      setSelectedComment(null);
      setSuccess('Yorum başarıyla güncellendi.');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
      
    } catch (err) {
      console.error('Error updating comment:', err);
      setError('Yorum güncellenirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteComment = async () => {
    if (!selectedComment) return;
    
    try {
      setLoading(true);
      
      const { error } = await supabase
        .from('comments')
        .delete()
        .eq('id', selectedComment.id);
      
      if (error) throw error;
      
      // Remove the comment from the list
      setComments(prev => prev.filter(comment => comment.id !== selectedComment.id));
      
      handleMenuClose();
      setSuccess('Yorum başarıyla silindi.');
      
      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccess(null);
      }, 3000);
      
    } catch (err) {
      console.error('Error deleting comment:', err);
      setError('Yorum silinirken bir hata oluştu.');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('tr-TR', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 2 }}>
        Yorumlar
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      {success && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {success}
        </Alert>
      )}
      
      {/* Comment input */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
          <TextField
            label="İsminiz"
            size="small"
            value={userName}
            onChange={(e) => setUserName(e.target.value)}
            sx={{ flex: 1 }}
          />
          <TextField
            label="E-posta (isteğe bağlı)"
            size="small"
            type="email"
            value={userEmail}
            onChange={(e) => setUserEmail(e.target.value)}
            sx={{ flex: 2 }}
          />
        </Box>
        
        <TextField
          fullWidth
          multiline
          rows={3}
          placeholder="Yorumunuzu buraya yazın..."
          value={newComment}
          onChange={(e) => setNewComment(e.target.value)}
          variant="outlined"
          sx={{ mb: 1 }}
        />
        
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant="contained"
            endIcon={loading ? <CircularProgress size={20} /> : <SendIcon />}
            onClick={handleSubmitComment}
            disabled={loading || !newComment.trim() || !userName.trim()}
          >
            Yorum Ekle
          </Button>
        </Box>
      </Box>
      
      <Divider sx={{ my: 3 }} />
      
      {/* Comments list */}
      {loading && comments.length === 0 ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress />
        </Box>
      ) : comments.length === 0 ? (
        <Typography color="text.secondary" sx={{ textAlign: 'center', p: 4 }}>
          Henüz yorum yapılmamış. İlk yorumu siz yapın!
        </Typography>
      ) : (
        <Box>
          {comments.map((comment) => (
            <CommentBox key={comment.id}>
              {editMode && selectedComment?.id === comment.id ? (
                <Box>
                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    value={editedComment}
                    onChange={(e) => setEditedComment(e.target.value)}
                    variant="outlined"
                    sx={{ mb: 2 }}
                  />
                  
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                    <Button 
                      variant="outlined" 
                      onClick={() => setEditMode(false)}
                      disabled={loading}
                    >
                      İptal
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleSaveEdit}
                      disabled={loading || !editedComment.trim()}
                    >
                      Kaydet
                    </Button>
                  </Box>
                </Box>
              ) : (
                <>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Avatar 
                        sx={{ 
                          width: 32, 
                          height: 32,
                          bgcolor: 'primary.main'
                        }}
                      >
                        {comment.user_name?.charAt(0).toUpperCase() || 'G'}
                      </Avatar>
                      <Box>
                        <Typography variant="subtitle2">
                          {comment.user_name || 'Misafir'}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {formatDate(comment.created_at)}
                          {comment.updated_at !== comment.created_at && ' (düzenlendi)'}
                        </Typography>
                      </Box>
                    </Box>
                    
                    {canEdit && (
                      <IconButton 
                        size="small"
                        onClick={(e) => handleMenuOpen(e, comment)}
                      >
                        <MoreVertIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Box>
                  
                  <Typography variant="body2" sx={{ pl: 5 }}>
                    {comment.content}
                  </Typography>
                </>
              )}
            </CommentBox>
          ))}
        </Box>
      )}
      
      {/* Comment menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleMenuClose}
      >
        <MenuItem onClick={handleEditComment}>
          <ListItemIcon>
            <EditIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>Düzenle</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDeleteComment}>
          <ListItemIcon>
            <DeleteIcon fontSize="small" color="error" />
          </ListItemIcon>
          <ListItemText sx={{ color: 'error.main' }}>Sil</ListItemText>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default CommentSection;
